import React, { InputHTMLAttributes } from "react";
import "../../styles/Global.css";

// Interface extending standard input props with additional custom properties
interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
}

/**
 * Reusable input field component with optional label and error message
 * Uses glassmorphism styling for a modern, translucent appearance
 *
 * @param {InputFieldProps} props - Component props including standard input attributes
 * @returns {JSX.Element} - Rendered input field component
 */
const InputField: React.FC<InputFieldProps> = ({
  label,
  error,
  className,
  icon,
  type = "text",
  ...props
}) => {
  // Generate unique ID for input-label association
  const id = React.useId();

  return (
    <div className="input-field-container" style={{ marginBottom: "20px" }}>
      {label && (
        <label
          htmlFor={id}
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: "14px",
            fontWeight: 600,
            color: "var(--theme-text-soft, #184342)",
            fontFamily: "'Raleway', sans-serif",
            letterSpacing: "0",
          }}
        >
          {label}
        </label>
      )}

      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}
      >
        <input
          id={id}
          type={type}
          style={{
            width: "100%",
            padding: "14px 16px",
            borderRadius: "12px",
            border: error
              ? "1px solid var(--theme-danger-text, rgba(159, 65, 65, 0.5))"
              : "1px solid var(--theme-border, rgba(32, 91, 90, 0.15))",
            fontSize: "14px",
            fontWeight: 450,
            fontFamily: "'Raleway', sans-serif",
            backgroundColor: "var(--theme-surface-strong, #ffffff)",
            boxShadow: error
              ? "0 0 0 3px var(--theme-danger-bg, rgba(159, 65, 65, 0.08))"
              : "0 2px 6px var(--theme-shadow, rgba(32, 91, 90, 0.04))",
            outline: "none",
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            paddingRight: icon ? "44px" : "16px",
            color: "var(--theme-text, #0f2b2a)",
          }}
          onFocus={(e) => {
            if (!error) {
              e.currentTarget.style.borderColor = "var(--theme-focus, #39a3a1)";
              e.currentTarget.style.boxShadow = "0 0 0 3px var(--theme-primary-tint, rgba(57, 163, 161, 0.12)), 0 2px 6px var(--theme-shadow, rgba(32, 91, 90, 0.08))";
            }
          }}
          onBlur={(e) => {
            if (!error) {
              e.currentTarget.style.borderColor = "var(--theme-border, rgba(32, 91, 90, 0.15))";
              e.currentTarget.style.boxShadow = "0 2px 6px var(--theme-shadow, rgba(32, 91, 90, 0.04))";
            }
          }}
          {...props}
        />

        {icon && (
          <div
            style={{
              position: "absolute",
              right: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              opacity: 0.6,
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.6"; }}
          >
            {icon}
          </div>
        )}
      </div>

      {error && (
        <p
          style={{
            color: "var(--theme-danger-text, #9f4141)",
            fontSize: "12px",
            marginTop: "6px",
            fontWeight: 500,
            fontFamily: "'Raleway', sans-serif",
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default InputField;
